import * as Sentry from '@sentry/react';
import Dexie, { liveQuery, type Table } from 'dexie';

import cable from '../cable';

import { ConnectionFreshnessTracker, type FreshnessReport, type FreshnessPhase, type RecoverySample } from './connectionFreshness';
import db from './db';
import { logDebug, logError } from './logger';

export interface CatchupEnvelopeRow {
  readonly id: number;
  readonly status: string;
  /** Opens the missed-data window — stamped when the tab went hidden, not when recovery began. */
  readonly since?: number;
  /** Closes the window; stamped at the moment the envelope becomes `ready` work. */
  readonly until?: number;
  readonly attempts?: number;
}

interface FreshnessDexieDatabase extends Dexie {
  readonly _catchup_envelopes: Table<CatchupEnvelopeRow, number>;
}

/**
 * A failed catchup envelope is recreated as a fresh `ready` one rather than being retried in
 * place, so `error` rows accumulate for a week and a single transient blip would otherwise read
 * as permanently stale. Repeated failure shows up as attempts climbing on work still outstanding.
 */
const STALE_ATTEMPTS = 3;

let tracker: ConnectionFreshnessTracker | undefined;

/** Summarizes outstanding catchup envelopes as a recovery sample. Pure, so it can be tested alone. */
export function recoverySampleFromEnvelopes(envelopes: readonly CatchupEnvelopeRow[]): RecoverySample {
  let oldestPendingSince: number | null = null;
  let failed = 0;

  for (const envelope of envelopes) {
    if ((envelope.attempts ?? 0) >= STALE_ATTEMPTS) failed += 1;
    // Lag measures how long recovery has been outstanding, which is `until` — the instant the
    // envelope was closed into `ready`. `since` opens the *missed-data* window instead, so
    // reading it would report a tab backgrounded overnight as eight hours behind the moment
    // catchup starts, in both the UI and the Sentry breadcrumb.
    const startedAt = typeof envelope.until === 'number' ? envelope.until : envelope.since;
    if (typeof startedAt !== 'number') continue;
    if (oldestPendingSince === null || startedAt < oldestPendingSince) oldestPendingSince = startedAt;
  }

  return { pending: envelopes.length, failed, oldestPendingSince };
}

/** Watches outstanding catchup work; envelope rows are the only recovery ledger the client keeps. */
function observeRecoveries(cb: (sample: RecoverySample) => void): () => void {
  const database = db as FreshnessDexieDatabase;
  const query = liveQuery(() => database._catchup_envelopes.where('status').equals('ready').toArray());
  const subscription = query.subscribe({
    next: (envelopes: CatchupEnvelopeRow[]) => cb(recoverySampleFromEnvelopes(envelopes)),
    error: (error: unknown) => logError('freshness', 'Error observing catchup envelopes', error)
  });

  return () => subscription.unsubscribe();
}

function handleTransition(next: FreshnessReport, previous: FreshnessPhase): void {
  logDebug('freshness', `${previous} -> ${next.phase}`, next);
  Sentry.addBreadcrumb({
    category: 'freshness',
    level: next.phase === 'stale' ? 'warning' : 'info',
    message: `${previous} -> ${next.phase}`,
    data: { ...next }
  });
}

/** Page-lifetime singleton; started on first use and never torn down outside tests. */
export function connectionFreshness(): ConnectionFreshnessTracker {
  if (tracker) return tracker;

  tracker = new ConnectionFreshnessTracker({
    eventTarget: window,
    initialCableConnected: cable.state === 'connected',
    initialNetworkOnline: navigator.onLine,
    observeRecoveries,
    onTransition: handleTransition
  });
  tracker.start();

  // Support/debug handle only; nothing here should be treated as a stable API.
  (window as unknown as { __debug__freshness?: () => FreshnessReport }).__debug__freshness = () =>
    tracker!.describe();

  return tracker;
}

/**
 * Routes the active feed's reconciliation result into the shared phase. The feed repairs its own
 * channel from a durable cursor, so its terminal failure is invisible to the envelope ledger.
 */
export function reportFeedFreshness(error: string | null | undefined): void {
  connectionFreshness().reportFeedFreshness(error);
}
